/* Copyright (c) 2025 MOCA Systems, Inc. All rights reserved. */

import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UPLOAD_PLAN_PRINT_IMAGE_ROUTE } from '../../services/rest-api-configuration/rest-api-routes';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { fancyDatabase } from '@touchplan/client-firebase';
import { TOUCHPLAN_API_CONTEXT } from '../../services/rest-api-configuration/touchplan-rest-interceptor';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map, mergeMap, toArray } from 'rxjs/operators';
import { IPlanPrintImage } from '../../app-data/constants/plan-print.model';

const UPLOAD_CONCURRENCY = 10;

@Injectable({ providedIn: 'root' })
export class PlanPrintApiService {
	private readonly http = inject(HttpClient);

	/** Generate a new plan print job id */
	public generatePlanPrintJobId(): string {
		const bsPath = fancyFirebaseGetters.getApiRequestsPath();
		const ref = fancyFirebaseReferencer.getRef(bsPath);
		return fancyDatabase.generateChildKey(ref);
	}

	/**
	 * Uploads multiple plan print image tiles to the backend API with a default concurrency of 10.
	 *  - Each image is uploaded via a separate HTTP POST request.
	 *  - Emits a single `void` value if all uploads succeed.
	 *  - If any upload fails then the observable errors immediately (fail-fast).
	 */
	public uploadPlanPrintImages(
		projectId: string,
		jobId: string,
		images: IPlanPrintImage[]
	): Observable<void> {
		return from(images).pipe(
			mergeMap((img) => this.uploadPlanPrintImage(projectId, jobId, img), UPLOAD_CONCURRENCY),
			// emit a single value after all uploads complete
			toArray(),
			map(() => void 0)
		);
	}

	/**
	 * Upload a captured image blob
	 */
	private uploadPlanPrintImage(
		projectId: string,
		jobId: string,
		imageData: IPlanPrintImage
	): Observable<void> {
		const file = new File([imageData.blob], `tile-${imageData.index}.png`, { type: 'image/png' });
		const formData = new FormData();
		formData.append('file', file);
		// .append only accepts strings, so we need to convert the order to a string
		formData.append('index', imageData.index.toString());
		formData.append('xCoord', imageData.position.x.toString());
		formData.append('yCoord', imageData.position.y.toString());
		formData.append('width', imageData.position.width.toString());
		formData.append('height', imageData.position.height.toString());

		return this.http
			.post(UPLOAD_PLAN_PRINT_IMAGE_ROUTE(projectId, jobId), formData, {
				context: new HttpContext().set(TOUCHPLAN_API_CONTEXT, {
					forceRefresh: false,
				}),
			})
			.pipe(map(() => void 0));
	}
}
